import { collection, doc, query, where, getDocs, deleteDoc } from 'firebase/firestore'
import { ref as storageRef, listAll, deleteObject } from 'firebase/storage'
import { httpsCallable } from 'firebase/functions'
import { db, storage, functions } from '@/firebase'
import { devLog } from '@/utils/environment'
import { SMOKE_TAGGED_COLLECTIONS, SMOKE_PROBE_DOCS } from './taggedCollections.js'

export { SMOKE_TAGGED_COLLECTIONS, SMOKE_PROBE_DOCS }

// Deletes every leftover smoke-test artifact (docs tagged __smokeTest, plus
// their storage files and the app_settings probe doc). Needed because some
// roles can CREATE smoke docs but lack delete permission (e.g. case managers
// can't delete students) — the Admin SDK callable sweeps those. Client sweep
// is the fallback until functions are deployed.
async function clientSweep() {
  const summary = {
    deleted: 0,
    storageDeleted: 0,
    leftoverDocs: 0,
    leftoverFiles: 0,
    errors: [],
    source: 'client'
  }

  for (const col of SMOKE_TAGGED_COLLECTIONS) {
    let snap
    try {
      snap = await getDocs(query(collection(db, col), where('__smokeTest', '==', true)))
    } catch (err) {
      summary.errors.push(`${col}: list failed — ${err.message}`)
      continue
    }
    summary.leftoverDocs += snap.size
    for (const d of snap.docs) {
      if (col === 'students') {
        for (const prefix of [
          `students/${d.id}/general`,
          `students/${d.id}/sensitive`,
          `encrypted-pdfs/${d.id}`
        ]) {
          try {
            const listing = await listAll(storageRef(storage, prefix))
            summary.leftoverFiles += listing.items.length
            for (const item of listing.items) {
              await deleteObject(item)
              summary.storageDeleted++
            }
          } catch {
            // Folder empty / no access — nothing to sweep.
          }
        }
      }
      try {
        await deleteDoc(d.ref)
        summary.deleted++
      } catch (err) {
        summary.errors.push(`${col}/${d.id}: ${err.message}`)
      }
    }
  }

  for (const [col, id] of SMOKE_PROBE_DOCS) {
    try {
      await deleteDoc(doc(db, col, id))
    } catch {
      // Usually doesn't exist — probes delete it themselves.
    }
  }

  return summary
}

async function clientCount() {
  const summary = { leftoverDocs: 0, leftoverFiles: 0, errors: [], source: 'client' }
  for (const col of SMOKE_TAGGED_COLLECTIONS) {
    try {
      const snap = await getDocs(query(collection(db, col), where('__smokeTest', '==', true)))
      summary.leftoverDocs += snap.size
    } catch (err) {
      summary.errors.push(`${col}: list failed — ${err.message}`)
    }
  }
  return summary
}

export async function countSmokeArtifacts() {
  try {
    const fn = httpsCallable(functions, 'cleanupSmokeArtifacts')
    const { data } = await fn({ dryRun: true })
    return {
      leftoverDocs: data.leftoverDocs || 0,
      leftoverFiles: data.leftoverFiles || 0,
      errors: data.errors || [],
      source: 'admin'
    }
  } catch (err) {
    const fallback = await clientCount()
    if (fallback.errors.length && fallback.leftoverDocs === 0) {
      return {
        leftoverDocs: null,
        leftoverFiles: null,
        errors: [`Count unavailable (${err.message})`, ...fallback.errors],
        source: 'unavailable'
      }
    }
    return fallback
  }
}

export async function cleanupSmokeArtifacts() {
  try {
    const fn = httpsCallable(functions, 'cleanupSmokeArtifacts')
    const { data } = await fn({ dryRun: false })
    const summary = {
      deleted: data.deleted || 0,
      storageDeleted: data.storageDeleted || 0,
      leftoverDocs: data.leftoverDocs || 0,
      leftoverFiles: data.leftoverFiles || 0,
      errors: data.errors || [],
      source: 'admin'
    }
    devLog('🧹 Smoke cleanup (admin):', summary)
    return summary
  } catch (err) {
    const fallback = await clientSweep()
    fallback.errors = [
      `Server cleanup unavailable (${err.message}). Used client sweep.`,
      ...fallback.errors
    ]
    devLog('🧹 Smoke cleanup (client fallback):', fallback)
    return fallback
  }
}
